import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getToken } from '../../utils/auth';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useParams } from 'react-router-dom';

const { VITE_API_SERVICE } = import.meta.env;

export default function ChangePassword() {
  const { userId } = useParams();
  const [selectedUser, setSelectedUser] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const token = getToken();

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await fetch(`${VITE_API_SERVICE}/v1/users/list`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      return data.users;
    }
  });

  useEffect(() => {
    if (userId) {
      setSelectedUser(userId);
    }
  }, [userId]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!selectedUser || !newPassword) return;

    setIsLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await fetch(
        `${VITE_API_SERVICE}/v1/users/change-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            userId: selectedUser,
            newPassword: newPassword
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to change password');
      }

      setNewPassword('');
      setSuccess(true);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="card bg-white border border-base-200">
      <div className="card-body">
        <div className="flex justify-between items-center mb-4">
          <h2 className="card-title text-lg">Change Password</h2>
        </div>

        {selectedUser && users?.find((u) => u._id === selectedUser) && (
          <div className="text-sm font-medium mb-4">
            Changing password for:{' '}
            <span className="text-base-content">
              {users?.find((u) => u._id === selectedUser)?.email}
            </span>
          </div>
        )}

        {!selectedUser && (
          <div className="text-center py-4">
            <p className="text-sm opacity-60">
              Select a user to change password for
            </p>
          </div>
        )}

        {selectedUser && (
          <form onSubmit={handleChangePassword} className="space-y-4 mt-4">
            {error && (
              <div className="alert alert-error flex justify-between items-center">
                <div className="flex items-center">
                  <XMarkIcon className="h-5 w-5" />
                  <span>{error}</span>
                </div>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setError(null)}
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            )}
            {success && (
              <div className="alert alert-success flex justify-between items-center">
                <span>Password changed successfully!</span>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setSuccess(false)}
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            )}
            <div className="form-control">
              <label className="label">
                <span className="label-text">New Password</span>
              </label>
              <input
                type="password"
                placeholder="••••••••"
                className="input input-bordered w-full"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setError(null);
                  setSuccess(false);
                }}
                required
              />
            </div>

            <div className="card-actions justify-end">
              <button
                type="submit"
                className="btn btn-ghost"
                disabled={isLoading}
              >
                {isLoading ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
